import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { Button, DateTimeInput, SpotPicker } from "@/ui";
import {
  useCreateTournament,
  useSpots,
  type TournamentFormat as TFormat,
  type SeedingMode,
} from "@/features/queries";
import { noAutofill } from "@/lib/noAutofill";

export function AdminTournamentNewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: spots = [] } = useSpots();
  const createMut = useCreateTournament();
  // Respect the caller's referrer so a "+" from /events drops the user
  // back on the events feed, not into the admin panel.
  const returnTo =
    (location.state as { from?: string } | null)?.from ?? "/admin/tournaments";

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [spotId, setSpotId] = useState<number | null>(null);
  const [dateTime, setDateTime] = useState<string | null>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(14, 0, 0, 0);
    return d.toISOString();
  });
  const [maxParticipants, setMaxParticipants] = useState("16");
  const [entryFee, setEntryFee] = useState("0");
  const [format, setFormat] = useState<TFormat>("single_elim");
  const [teamSize, setTeamSize] = useState<1 | 2>(1);
  const [seeding, setSeeding] = useState<SeedingMode>("random");
  const [bestOf, setBestOf] = useState("1");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (spotId == null || !dateTime) return;
    try {
      await createMut.mutateAsync({
        name: name.trim(),
        description: description.trim() || null,
        spot_id: spotId,
        date_time: dateTime,
        max_participants: Number(maxParticipants),
        entry_fee: Number(entryFee),
        format,
        team_size: teamSize,
        seeding_mode: seeding,
        best_of: Number(bestOf),
      });
      navigate(returnTo);
    } catch {
      /* error shown below */
    }
  };

  return (
    <>
      <button
        onClick={() => navigate(returnTo)}
        style={{
          background: "transparent",
          border: 0,
          color: "var(--fg2)",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: 0,
          marginBottom: 12,
          fontFamily: "var(--font-body)",
          fontSize: 13,
        }}
      >
        <ArrowLeft size={14} /> назад
      </button>

      <h1 className="h1" style={{ marginBottom: 16 }}>Новый турнир</h1>

      <form
        onSubmit={onSubmit}
        style={{
          background: "var(--bg-section)",
          borderRadius: "var(--r-lg)",
          padding: 24,
          maxWidth: 640,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <FormField label="Название">
          <TextInput value={name} onChange={setName} required />
        </FormField>

        <FormField label="Описание">
          <TextArea value={description} onChange={setDescription} rows={3} />
        </FormField>

        <FormField label="Площадка">
          <SpotPicker spots={spots} value={spotId} onChange={setSpotId} />
          {spots.length === 0 && (
            <p className="caption" style={{ color: "var(--fg-danger)", marginTop: 4 }}>
              Ни одной площадки. Добавь через /admin/spots.
            </p>
          )}
        </FormField>

        <FormField label="Дата и время">
          <DateTimeInput value={dateTime} onChange={setDateTime} required />
        </FormField>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <FormField label="Макс. участников">
            <TextInput type="number" value={maxParticipants} onChange={setMaxParticipants} required min={2} max={256} />
          </FormField>
          <FormField label="Взнос, ₽">
            <TextInput type="number" value={entryFee} onChange={setEntryFee} required min={0} />
          </FormField>
        </div>

        <FormField label="Формат сетки">
          <select value={format} onChange={(e) => setFormat(e.target.value as TFormat)} style={inputStyle}>
            <option value="single_elim">Single-elimination</option>
            <option value="double_elim">Double-elimination</option>
            <option value="round_robin">Round-robin (каждый с каждым)</option>
          </select>
        </FormField>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          <FormField label="Тип">
            <select value={teamSize} onChange={(e) => setTeamSize(Number(e.target.value) as 1 | 2)} style={inputStyle}>
              <option value={1}>Одиночный</option>
              <option value={2}>Парный (2×2)</option>
            </select>
          </FormField>
          <FormField label="Посев">
            <select value={seeding} onChange={(e) => setSeeding(e.target.value as SeedingMode)} style={inputStyle}>
              <option value="rating">По рейтингу</option>
              <option value="random">Случайно</option>
              <option value="manual">Вручную</option>
            </select>
          </FormField>
          <FormField label="До победы в">
            <select value={bestOf} onChange={(e) => setBestOf(e.target.value)} style={inputStyle}>
              <option value="1">1 игре</option>
              <option value="3">3 играх (best-of-3)</option>
              <option value="5">5 играх (best-of-5)</option>
              <option value="7">7 играх (best-of-7)</option>
            </select>
          </FormField>
        </div>

        {createMut.error && (
          <div style={{
            background: "rgba(223,63,64,0.1)",
            color: "var(--fg-danger)",
            padding: "8px 12px",
            borderRadius: "var(--r-md)",
            fontSize: 13,
          }}>
            {(createMut.error as Error).message}
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="submit"
            disabled={createMut.isPending || !name || spotId == null || !dateTime}
          >
            {createMut.isPending ? "Создаём…" : "Создать"}
          </Button>
          <Button variant="ghost" onClick={() => navigate(returnTo)}>
            Отмена
          </Button>
        </div>
      </form>
    </>
  );
}

const inputStyle: React.CSSProperties = {
  border: "1px solid var(--separator)",
  borderRadius: "var(--r-md)",
  padding: "10px 14px",
  fontFamily: "var(--font-body)",
  fontSize: 15,
  background: "var(--bg)",
  color: "var(--fg1)",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

// <div>, not <label>: composite pickers (SpotPicker) render a trigger button
// plus a search input; a surrounding <label> delegates clicks to the first
// focusable child, which in Yandex/some Chromium variants swallows clicks
// on picker rows.
function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span className="label" style={{ color: "var(--fg2)" }}>{label}</span>
      {children}
    </div>
  );
}

function TextInput({
  value,
  onChange,
  type = "text",
  min,
  max,
  required,
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  min?: number;
  max?: number;
  required?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      min={min}
      max={max}
      required={required}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle}
      {...noAutofill}
    />
  );
}

function TextArea({
  value,
  onChange,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inputStyle, resize: "vertical", fontFamily: "var(--font-body)" }}
    />
  );
}
